import {
  ActionIcon,
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Group,
  List,
  Loader,
  Modal,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconDownload,
  IconEdit,
  IconExclamationCircle,
  IconPlugConnected,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconShieldCheck,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useEvDcModbusTargets } from "@/api/queries";
import {
  EV_DC_MODBUS_PROFILES,
  type EvDcModbusProfile,
  type EvDcModbusTarget,
} from "@/api/types";
import { ErrorBanner } from "@/components/ErrorBanner";
import { EvDcModbusTargetDeleteConfirm } from "@/components/EvDcModbusTargetDeleteConfirm";
import { EvDcModbusTargetExportModal } from "@/components/EvDcModbusTargetExportModal";
import { EvDcModbusTargetFormModal } from "@/components/EvDcModbusTargetFormModal";
import { SiteSelect } from "@/components/SiteSelect";

const PAGE_SIZE = 50;

const PROFILE_OPTIONS: { value: EvDcModbusProfile; label: string }[] = [
  { value: "nidec", label: "Nidec" },
  { value: "cjnoo-gun1", label: "CJNOO gun 1" },
  { value: "cjnoo-gun2", label: "CJNOO gun 2" },
];

type FiltersState = {
  site_slug: string;
  device_slug: string;
  profile: string;
  writes_enabled: string;
};

const EMPTY_FILTERS: FiltersState = {
  site_slug: "",
  device_slug: "",
  profile: "",
  writes_enabled: "",
};

type ModalState =
  | { kind: "none" }
  | { kind: "export" }
  | { kind: "create" }
  | { kind: "edit"; target: EvDcModbusTarget }
  | { kind: "delete"; target: EvDcModbusTarget };

export function AdminEvDcModbusTargetsPage() {
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<ModalState>({ kind: "none" });
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const queryParams = useMemo(
    () => ({
      site_slug: filters.site_slug.trim() || undefined,
      device_slug: filters.device_slug.trim() || undefined,
      profile: asProfile(filters.profile),
      writes_enabled:
        filters.writes_enabled === ""
          ? undefined
          : filters.writes_enabled === "true",
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [filters, page],
  );
  const exportParams = useMemo(
    () => ({
      site_slug: filters.site_slug.trim() || undefined,
      device_slug: filters.device_slug.trim() || undefined,
      profile: asProfile(filters.profile),
      writes_enabled:
        filters.writes_enabled === ""
          ? undefined
          : filters.writes_enabled === "true",
      include_write_flags: false,
      limit: 200,
      offset: 0,
    }),
    [filters],
  );

  const targetsQuery = useEvDcModbusTargets(queryParams);
  const items = targetsQuery.data?.items ?? [];
  const total = targetsQuery.data?.page.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setFilter = (k: keyof FiltersState, v: string) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  return (
    <Stack>
      <Breadcrumbs>
        <Text>Admin</Text>
        <Text>EV DC Modbus targets</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={1}>EV DC Modbus targets</Title>
            <Tooltip label="EV DC target setup help">
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="EV DC target setup help"
                onClick={() => setInstructionsOpen(true)}
              >
                <IconExclamationCircle size={20} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Text c="dimmed" size="sm">
            Adapter connection records for active EV chargers.
          </Text>
        </Stack>
        <Group>
          <Tooltip label="Refresh list">
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => targetsQuery.refetch()}
              loading={targetsQuery.isFetching}
              aria-label="Refresh"
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            onClick={() => setModal({ kind: "export" })}
          >
            Export setup JSON
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setModal({ kind: "create" })}
          >
            Add target
          </Button>
        </Group>
      </Group>

      <Alert
        icon={<IconShieldCheck size={18} />}
        color="green"
        variant="light"
        title="Control writes stay off"
      >
        Adding or editing a target here does not start live Modbus writes. Enabling live control
        remains a separate onsite release step.
      </Alert>

      <Paper withBorder p="md" radius="md">
        <Group gap="sm" wrap="wrap">
          <SiteSelect
            label="Site"
            placeholder="any"
            clearable
            value={filters.site_slug || null}
            onChange={(v) => setFilter("site_slug", v ?? "")}
            w={240}
          />
          <TextInput
            label="EV charger ID"
            placeholder="e.g. ev_charger_1"
            leftSection={<IconSearch size={14} />}
            value={filters.device_slug}
            onChange={(e) => setFilter("device_slug", e.currentTarget.value)}
            w={200}
          />
          <Select
            label="Connector profile"
            placeholder="any"
            data={PROFILE_OPTIONS}
            clearable
            value={filters.profile || null}
            onChange={(v) => setFilter("profile", v ?? "")}
            w={180}
          />
          <Select
            label="Control"
            placeholder="any"
            data={[
              { value: "false", label: "Off" },
              { value: "true", label: "On" },
            ]}
            clearable
            value={filters.writes_enabled || null}
            onChange={(v) => setFilter("writes_enabled", v ?? "")}
            w={160}
          />
        </Group>
      </Paper>

      {targetsQuery.error && <ErrorBanner error={targetsQuery.error} />}

      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconPlugConnected size={20} />
            <Title order={3}>Results</Title>
          </Group>
          <Text c="dimmed" size="sm">
            {total} target{total === 1 ? "" : "s"}
          </Text>
        </Group>

        {targetsQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : items.length === 0 ? (
          <Text c="dimmed">No EV DC Modbus targets match the current filters.</Text>
        ) : (
          <ScrollArea>
            <Table highlightOnHover verticalSpacing="sm" miw={980}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Target</Table.Th>
                  <Table.Th>Site / EV charger</Table.Th>
                  <Table.Th>Connection</Table.Th>
                  <Table.Th>Connector</Table.Th>
                  <Table.Th>Network timing</Table.Th>
                  <Table.Th>Safety limits</Table.Th>
                  <Table.Th>Control</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((target) => (
                  <Table.Tr key={target.id}>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text fw={500}>{target.name}</Text>
                        <Text c="dimmed" size="xs" ff="monospace">
                          {target.external_id_prefix}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm">
                          <Link to={`/sites/${target.site_slug}`}>{target.site_slug}</Link>
                        </Text>
                        <Text c="dimmed" size="xs">
                          <Link to={`/sites/${target.site_slug}/devices/${target.device_slug}`}>
                            {target.device_slug}
                          </Link>
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm" ff="monospace">
                          {target.host}:{target.port}
                        </Text>
                        <Text c="dimmed" size="xs">
                          unit {target.unit_id}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={profileColor(target.profile)}>
                        {profileLabel(target.profile)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm">Timeout {target.timeout_seconds}s</Text>
                        <Text c="dimmed" size="xs">
                          Address adjustment {target.address_offset}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm">
                          {target.dlm_safe_current_a} A fallback after{" "}
                          {target.dlm_comm_timeout_s}s
                        </Text>
                        <Text c="dimmed" size="xs">
                          Check after {target.verify_readback_delay_ms}ms,{" "}
                          {target.verify_retries} retry
                          {target.verify_retries === 1 ? "" : "s"}, {target.verify_tolerance} A
                          allowed difference
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={target.writes_enabled ? "orange" : "gray"}
                      >
                        {target.writes_enabled ? "On" : "Off"}
                      </Badge>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Tooltip label="Edit">
                          <ActionIcon
                            variant="subtle"
                            aria-label={`Edit ${target.name}`}
                            onClick={() => setModal({ kind: "edit", target })}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={`Delete ${target.name}`}
                            onClick={() => setModal({ kind: "delete", target })}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Paper>

      <EvDcModbusTargetFormModal
        opened={modal.kind === "create"}
        onClose={() => setModal({ kind: "none" })}
        mode="create"
      />
      <EvDcModbusTargetExportModal
        opened={modal.kind === "export"}
        onClose={() => setModal({ kind: "none" })}
        params={exportParams}
      />
      <EvDcModbusTargetFormModal
        opened={modal.kind === "edit"}
        onClose={() => setModal({ kind: "none" })}
        mode="edit"
        target={modal.kind === "edit" ? modal.target : undefined}
      />
      <EvDcModbusTargetDeleteConfirm
        opened={modal.kind === "delete"}
        onClose={() => setModal({ kind: "none" })}
        target={modal.kind === "delete" ? modal.target : null}
      />
      <Modal
        opened={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
        title="EV DC target setup help"
        size="lg"
      >
        <Stack>
          <Text size="sm">
            Use this page to store the adapter connection details supplied during onsite
            commissioning. These records are kept in EMS for review and export, but saving them
            does not activate live charger control.
          </Text>
          <List spacing="sm" size="sm">
            <List.Item>
              Select the site and the active EV charger device that this adapter controls.
            </List.Item>
            <List.Item>
              Enter the adapter network address, port, unit number, and connector profile from the
              commissioning sheet.
            </List.Item>
            <List.Item>
              Safety values define future fallback behavior: current limit, communication timeout,
              and command read-back checks.
            </List.Item>
            <List.Item>
              The setup JSON export is for manual deployment review and always keeps control writes
              off by default.
            </List.Item>
          </List>
        </Stack>
      </Modal>
    </Stack>
  );
}

function asProfile(value: string): EvDcModbusProfile | undefined {
  return (EV_DC_MODBUS_PROFILES as readonly string[]).includes(value)
    ? (value as EvDcModbusProfile)
    : undefined;
}

function profileLabel(profile: EvDcModbusProfile): string {
  return PROFILE_OPTIONS.find((option) => option.value === profile)?.label ?? profile;
}

function profileColor(profile: EvDcModbusProfile): string {
  if (profile === "nidec") return "green";
  return "blue";
}
